import type { DashboardLayout, WidgetType } from '@sea-snag/shared';
import {
  Badge,
  Button,
  ScrollArea,
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@sea-snag/ui';
import { Check, Plus } from 'lucide-react';
import { useMemo } from 'react';

import { useCanSeeAdmin } from '../../hooks/use-can-see-admin';
import { addWidget, closeCatalog } from '../../stores/dashboard-store';
import {
  visibleWidgets,
  WIDGET_CATEGORIES,
  type WidgetCategory,
  type WidgetRegistration,
} from './widget-registry';

// ── Size labels ──────────────────────────────────────────────────────

const SIZE_LABELS = {
  third: '1/3 width',
  half: '1/2 width',
  full: 'Full width',
} as const;

const CATEGORY_ICON_COLORS: Record<WidgetCategory, string> = {
  stat: 'text-blue-500',
  chart: 'text-violet-500',
  list: 'text-amber-500',
  analysis: 'text-emerald-500',
  operational: 'text-rose-500',
};

// ── Widget Catalog ───────────────────────────────────────────────────

interface WidgetCatalogProps {
  layout: DashboardLayout;
  isCatalogOpen: boolean;
}

export function WidgetCatalog({ layout, isCatalogOpen }: WidgetCatalogProps) {
  const canSeeAdmin = useCanSeeAdmin();

  const addedTypes = useMemo(
    () => new Set<WidgetType>(layout.widgets.map((w) => w.type)),
    [layout.widgets]
  );

  const groupedWidgets = useMemo(() => {
    const groups: Record<WidgetCategory, WidgetRegistration[]> = {
      stat: [],
      chart: [],
      list: [],
      analysis: [],
      operational: [],
    };

    for (const registration of visibleWidgets(canSeeAdmin)) {
      groups[registration.category].push(registration);
    }

    return groups;
  }, [canSeeAdmin]);

  return (
    <Sheet open={isCatalogOpen} onOpenChange={(open) => !open && closeCatalog()}>
      <SheetContent side="right" className="w-full sm:max-w-md">
        <SheetHeader>
          <SheetTitle>Add Widget</SheetTitle>
          <SheetDescription>Choose widgets to add to your dashboard</SheetDescription>
        </SheetHeader>
        <ScrollArea className="flex-1 px-4">
          <div className="space-y-6 pb-4">
            {WIDGET_CATEGORIES.map((category) => {
              const widgets = groupedWidgets[category.id];
              if (widgets.length === 0) return null;

              return (
                <div key={category.id} className="space-y-3">
                  <div>
                    <h3 className="text-sm font-medium">{category.label}</h3>
                    <p className="text-muted-foreground text-xs">{category.description}</p>
                  </div>
                  <div className="space-y-2">
                    {widgets.map((registration) => {
                      const isAdded = addedTypes.has(registration.type);
                      const Icon = registration.icon;

                      return (
                        <div
                          key={registration.type}
                          className={`flex items-start gap-3 rounded-lg p-3 transition-colors ${
                            isAdded
                              ? 'bg-primary/5 border-primary/20 border opacity-60'
                              : 'bg-muted/50 hover:bg-muted cursor-pointer'
                          }`}
                          onClick={!isAdded ? () => addWidget(registration.type) : undefined}
                        >
                          <Icon
                            className={`mt-0.5 size-4 shrink-0 ${CATEGORY_ICON_COLORS[registration.category]}`}
                          />
                          <div className="min-w-0 flex-1">
                            <div className="flex items-center gap-2">
                              <span className="text-sm font-medium">{registration.name}</span>
                              <Badge variant="outline" className="text-[10px]">
                                {SIZE_LABELS[registration.size]}
                              </Badge>
                              {isAdded && (
                                <Badge variant="secondary" className="text-[10px]">
                                  <Check className="mr-0.5 size-2.5" />
                                  Added
                                </Badge>
                              )}
                            </div>
                            <p className="text-muted-foreground mt-0.5 text-xs">
                              {registration.description}
                            </p>
                          </div>
                          <Button
                            variant="outline"
                            size="icon-xs"
                            disabled={isAdded}
                            onClick={(e) => {
                              e.stopPropagation();
                              addWidget(registration.type);
                            }}
                          >
                            <Plus className="size-3" />
                          </Button>
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        </ScrollArea>
      </SheetContent>
    </Sheet>
  );
}
